import { createSelector } from '@reduxjs/toolkit'
import { assembleForm } from 'apps/builder/lib/transforms'
import { orderByPosition } from 'apps/builder/store/helpers'
import {
  whiteListedTypesForCalculation,
  conditionEntryTypeWhiteList,
  eligibleForReferenceData,
  blackListedTypesForMirror,
  whiteListedTypesForPaymentField,
  paymentDataToIncludeBlacklist,
  checkNumberStyleForPaymentTotalField,
  invisibleFieldTypes,
  isTrueStringMatch,
  isTable,
  getAllDescendants,
} from 'apps/builder/lib/utils'
import {
  isLoopScreen,
  isStandardScreen,
  findChildSheets,
} from 'apps/builder/components/workspace-new/sheets/helpers'
import { selectors, lib } from '@gocanvas/client'
const {
  isEligibleKeyField,
  keyFieldEligibleTypes,
  ADVANCED_LOOP_SHEET_TYPE_ID,
  LOOP_SHEET_TYPE_ID,
  LABELS_ABOVE_SHEET_TYPE_ID,
  LABELS_LEFT_SHEET_TYPE_ID,
} = lib
export const { conditionsSelector, clientEntriesSelector } = selectors

export const pastSelector = ({ past }) => past
export const presentSelector = ({ present }) => present
export const futureSelector = ({ future }) => future

export const featureFlagsSelector = ({ featureFlags }) => featureFlags

export const entities = ({ entities }) => entities
export const types = ({ types }) => types

export const settings = ({ settings }) => settings

export const referenceImagesSelector = ({ referenceImages }) => referenceImages

export const referenceDataFeatureFlag = createSelector(
  featureFlagsSelector,
  ({ builder_3_reference_data }) => builder_3_reference_data || false
)

export const referenceDataUploadFeatureFlag = createSelector(
  featureFlagsSelector,
  ({ builder_3_reference_data_upload }) =>
    builder_3_reference_data_upload || false
)

export const importFormFeatureFlagSelector = createSelector(
  featureFlagsSelector,
  ({ import_form }) => import_form || false
)

export const outlineFeatureFlagSelector = createSelector(
  featureFlagsSelector,
  ({ outline }) => outline || false
)

export const newWorkspaceFeatureFlagSelector = createSelector(
  featureFlagsSelector,
  ({ new_workspace }) => new_workspace || false
)

export const entitiesSelector = createSelector(entities, presentSelector)
export const modifiedSelector = createSelector(
  entities,
  ({ past }) => past.length
)
export const unsavedChangesSelector = createSelector(
  settings,
  ({ unsavedChanges }) => unsavedChanges || false
)

export const activeSheetDepthSelector = createSelector(
  settings,
  ({ activeSheetDepth }) => activeSheetDepth || 0
)

export const panelOverridesSelector = createSelector(
  settings,
  ({ panelOverrides }) => panelOverrides || {}
)

export const entriesSelector = createSelector(
  entitiesSelector,
  ({ entries }) => entries
)

export const operationsSelector = createSelector(
  entitiesSelector,
  ({ operations }) => operations
)

export const conditionTypesSelector = createSelector(
  types,
  ({ conditionTypes }) => conditionTypes
)

export const operationTypesSelector = createSelector(
  types,
  ({ operationTypes }) => operationTypes
)

export const referenceDataEntries = createSelector(entriesSelector, entries =>
  entries.filter(
    ({ reference_data_id, reference_data_column }) =>
      reference_data_id && reference_data_column
  )
)

export const liveDataEntries = createSelector(entriesSelector, entries =>
  entries.filter(
    ({ live_data_source_id, live_data_source_column }) =>
      live_data_source_id && live_data_source_column
  )
)

const activeGuidCheck = (entries, activeId) =>
  entries.find(entry => entry.guid == activeId)

const activeIdCheck = (items, activeId) =>
  items.find(item => item.id == activeId)

export const appSelector = createSelector(entitiesSelector, ({ app }) => app)

export const appNameSelector = createSelector(
  entitiesSelector,
  ({ app }) => app.name
)

export const defaultAppNameSelector = createSelector(
  entitiesSelector,
  ({ app }) => app.defaultAppName
)

export const appStatusSelector = createSelector(
  entitiesSelector,
  ({ app }) => app.status
)

export const appIDSelector = createSelector(
  entitiesSelector,
  ({ app }) => app.id
)

export const appVersionSelector = createSelector(
  entitiesSelector,
  ({ app }) => app.version
)

export const formLogoSelector = createSelector(
  entitiesSelector,
  ({ app }) => app.logo
)

export const sheetsSelector = createSelector(
  entitiesSelector,
  ({ sheets }) => sheets
)

export const previewEntriesSelector = ({ previewValues }) => previewValues

export const loopPreviewValuesSelector = createSelector(
  previewEntriesSelector,
  entries => entries.filter(entry => entry.parentGuid).sort(orderByPosition)
)

export const settingsSelector = ({ settings }) => settings
export const activeEntryIdSelector = ({ settings }) => settings.activeEntry
export const fullScreenPreviewSelector = ({ settings }) =>
  settings.fullScreenPreview
export const fullScreenWorkspaceSelector = ({ settings }) =>
  settings.fullScreenWorkspace

export const showLeftPanelSelector = ({ settings }) => settings.showLeftPanel

export const activeLeftPanelTabSelector = ({ settings }) =>
  settings.activeLeftPanelTab

export const doesFieldContainRefDataSelector = createSelector(
  activeEntryIdSelector,
  referenceDataEntries,
  (activeEntry, refEntries) => {
    return refEntries.find(ref => ref.guid == activeEntry) ? true : false
  }
)

export const inBuilder3ExperienceSelector = ({ settings }) =>
  settings.in_builder_3_experience

export const modalSelector = ({ settings }) => settings.modalOpen
export const activeModalNameSelector = ({ settings }) => settings.activeModal
export const activePreviewSelector = ({ settings }) => settings.activePreview
export const activeSheetIdSelector = ({ settings }) => settings.activeSheet
export const isFormPublishableSelector = ({ settings }) =>
  settings.is_form_publishable
export const isWebFormPublishableSelector = ({ settings }) =>
  settings.is_web_form_publishable
export const numOfPublishedFormsSelector = ({ settings }) =>
  settings.num_of_published_forms
export const numOfPublishedWebFormsSelector = ({ settings }) =>
  settings.num_of_published_web_forms
export const lastActionSelector = ({ settings }) => settings.lastAction
export const nextFormIdSelector = ({ settings }) => settings.next_form_id
export const isSupportSelector = ({ settings }) => settings.isSupport
export const reloadAppSelector = ({ settings }) => settings.reload_app
export const readOnlySelector = ({ settings }) => settings.readOnly
export const pingIntervalSelector = ({ workspaceConfig }) =>
  workspaceConfig.pingInterval
export const builderTypeSelector = ({ workspaceConfig }) =>
  workspaceConfig.builderType
export const projectSupportSelector = ({ workspaceConfig }) =>
  workspaceConfig.projectSupport
export const isAppSavingSelector = ({ settings }) => settings.isAppSaving

export const settingsModalSelector = createSelector(
  fullScreenPreviewSelector,
  modalSelector,
  (fullScreen, modal) => !fullScreen && modal
)

export const referenceDataFileListSelector = ({ referenceData }) =>
  referenceData

export const liveDataSourcesSelector = ({ liveDataSources = []  }) => liveDataSources

export const dataFileSourcesSelector = createSelector(
  referenceDataFileListSelector,
  liveDataSourcesSelector,
  (refDataFiles, liveDataSources) => [...refDataFiles, ...liveDataSources.map(source => ({ ...source, type: 'live_data' }))]
)

export const previewModalSelector = createSelector(
  fullScreenPreviewSelector,
  modalSelector,
  (fullScreen, modal) => fullScreen && modal
)

export const flashMessageSelector = createSelector(
  settingsSelector,
  ({ flashMessage }) => flashMessage
)

export const activeEntrySelector = createSelector(
  entriesSelector,
  activeEntryIdSelector,
  activeGuidCheck
)

export const activeSheetSelector = createSelector(
  sheetsSelector,
  activeSheetIdSelector,
  activeIdCheck
)

export const maxScreenNestingDepthSelector = createSelector(
  settings,
  ({ maxScreenNestingDepth }) => maxScreenNestingDepth || -1
)

export const activeSheetEntriesSelector = createSelector(
  entriesSelector,
  activeSheetIdSelector,
  (entries, id) =>
    entries.filter(({ sheet_id }) => sheet_id === id).sort(orderByPosition)
)

export const allEntryTypesSelector = createSelector(
  types,
  ({ entryTypes }) => entryTypes
)

export const entryTypesSelector = createSelector(
  allEntryTypesSelector,
  entryTypes => {
    const visibleTypes = entryTypes.filter(({ description }) => {
      return !invisibleFieldTypes.includes(description)
    })

    return visibleTypes
  }
)

export const alphaEntryTypesSelector = createSelector(
  entryTypesSelector,
  visibleTypes => {
    return visibleTypes.sort((a, b) => {
      return a.type.localeCompare(b.type)
    })
  }
)

export const sectionsSelector = createSelector(
  entitiesSelector,
  ({ sections }) => sections
)

export const multiSectionIds = createSelector(sheetsSelector, sheets =>
  sheets.reduce((acc, { multi_section }) => {
    if (multi_section) {
      acc.push(multi_section)
    }
    return acc
  }, [])
)

// top-level sheets (those not in a multi_section)
export const primarySheetsSelector = createSelector(
  sheetsSelector,
  multiSectionIds,
  (sheets, multiSectionIds) =>
    sheets.filter(({ section_id }) => !multiSectionIds.includes(section_id))
)

export const subSheetsSelector = createSelector(
  sheetsSelector,
  multiSectionIds,
  (sheets, multiSectionIds) =>
    sheets.filter(({ section_id }) => multiSectionIds.includes(section_id))
)

export const combinedSheetSelector = createSelector(
  primarySheetsSelector,
  subSheetsSelector,
  (primarySheets, subSheets) => {
    return primarySheets.map(sheet => {
      return {
        ...sheet,
        subSheets: subSheets
          .reduce((acc, { section_id, id }) => {
            if (sheet.multi_section === section_id) {
              acc.push(id)
            }
            return acc
          }, []),
      }
    })
  }
)

// Finds all simple loop / table sheets (even nested ones) and their children
export const simpleLoopCombinedSheetSelector = createSelector(
  sheetsSelector,
  subSheetsSelector,
  (allSheets, subSheets) => {
    return allSheets.reduce((acc, sheet) => {
      const { sheet_type_id, multi_section } = sheet
      if (sheet_type_id === LOOP_SHEET_TYPE_ID) {
        const updatedSheet = {
          ...sheet,
          subSheets: subSheets.reduce((subAcc, { section_id, id }) => {
            if (multi_section === section_id) {
              subAcc.push(id)
            }
            return subAcc
          }, [])
        }
        acc.push(updatedSheet)
      }
      return acc
    }, [])
  }
)

// Finds all advanced loop sheets and traverses to find/include all nested child sheets
export const advancedLoopCombinedSheetSelector = createSelector(
  primarySheetsSelector,
  sheetsSelector,
  (primarySheets, allSheets) => {
    return primarySheets.reduce((acc, sheet) => {
      const { sheet_type_id, multi_section } = sheet
      if (sheet_type_id === ADVANCED_LOOP_SHEET_TYPE_ID) {
        acc.push({
          ...sheet,
          subSheets: findChildSheets(allSheets, multi_section),
        })
      }
      return acc
    }, [])
  }
)

// Identifies if the activeSheet is part of an advanced loop, regardless of whether the activeSheet
// is a parent or a child, and returns the advanced loop sheet with all nested child sheets.
export const activeAdvancedLoopSheetSelector = createSelector(
  advancedLoopCombinedSheetSelector,
  activeSheetSelector,
  (advancedLoopSheets, activeSheet) => {
    if (!activeSheet) return false

    return advancedLoopSheets.find(
      sheet =>
        sheet.id == activeSheet.id ||
        sheet.subSheets.map(({ id }) => id).includes(activeSheet.id)
    )
  }
)

// Finds all parents in the hierarchy of the activeSheet; useful for when the activeSheet
// is a nested child of an advanced loop sheet
export const activeSheetParentHierarchySelector = createSelector(
  activeSheetSelector,
  sheetsSelector,
  (activeSheet, allSheets) => {
    if (!activeSheet) return []

    const parentIds = []
    let foundParent = allSheets.find(
      sheet => sheet.multi_section == activeSheet.section_id
    )

    while (foundParent) {
      parentIds.push(foundParent.id)
      foundParent = allSheets.find(
        sheet => sheet.multi_section == foundParent.section_id
      )
    }

    return parentIds
  }
)

export const sectionPreviewSelector = createSelector(
  sectionsSelector,
  combinedSheetSelector,
  (sections, sheets) => {
    const getSectionInformation = ({ section_id, ...sheet }) => {
      const { position } = sections.find(({ id }) => id === section_id) || {}
      const updateSheet = {
        ...sheet,
        section_id,
        position,
      }
      return updateSheet
    }

    return sheets.map(getSectionInformation).sort(orderByPosition)
  }
)

// Returns all standard sheets
export const standardSheetsSelector = createSelector(sheetsSelector, sheets => {
  return sheets.filter(sheet => isStandardScreen(sheet) && !sheet.multi_section)
})

// Returns only primary (top-level) standard sheets
export const standardPrimarySheetsSelector = createSelector(
  primarySheetsSelector,
  sheets => {
    return sheets.filter(
      sheet => isStandardScreen(sheet) && !sheet.multi_section
    )
  }
)

export const standardSheetEntriesSelector = createSelector(
  entriesSelector,
  standardPrimarySheetsSelector,
  (entries, sheets) => {
    const sheetIds = new Set(sheets.map(({ id }) => id))
    return entries.filter(({ sheet_id }) =>
      sheetIds.has(sheet_id)
    )
  }
)

export const previousScreensSelector = createSelector(
  activeSheetSelector,
  primarySheetsSelector,
  subSheetsSelector,
  simpleLoopCombinedSheetSelector,
  advancedLoopCombinedSheetSelector,
  sectionsSelector,
  (
    activeSheet,
    primarySheets,
    subSheets,
    simpleLoopCombinedSheets,
    advancedLoopCombinedSheets,
    sections
  ) => {
    const topLevelSheetIds = primarySheets.map(({ id }) => `${id}`)
    const subSheetIds = subSheets.map(({ id }) => `${id}`)
    const sectionPositionMap = sections.reduce(
      (acc, section) => ({
        ...acc,
        [section.id]: section.position,
      }),
      {}
    )

    let activeSheetPosition = sectionPositionMap[activeSheet.section_id]
    if (subSheetIds.includes(`${activeSheet.id}`)) {
      // Active sheet is nested, find the top level sheet and use that to compare positions
      const combinedLoopSheets = [
        ...simpleLoopCombinedSheets,
        ...advancedLoopCombinedSheets.map(combinedSheet => ({
          ...combinedSheet,
          subSheets: combinedSheet.subSheets.map(({ id }) => `${id}`),
        })),
      ]

      // Filters out any loop sheets that are nested; we only want top level sheets
      const topLevelSheet = combinedLoopSheets.find(sheet => 
        topLevelSheetIds.includes(`${sheet.id}`) && sheet.subSheets.includes(`${activeSheet.id}`)
    )

      activeSheetPosition = sectionPositionMap[topLevelSheet.section_id]
    }

    // Return all previous top level sheets
    return primarySheets.filter(
      ({ section_id }) => sectionPositionMap[section_id] < activeSheetPosition
    )
  }
)

export const tableCalculationSheetSelector = createSelector(
  previousScreensSelector,
  combinedSheetSelector,
  (previousSheets, combinedSheetSelector) => {
    const previousSheetIds = previousSheets.map(({ id }) => `${id}`)
    const topLevelTableSheets = combinedSheetSelector.filter(
      sheet => isTable(sheet) && previousSheetIds.includes(`${sheet.id}`)
    )
    return topLevelTableSheets
  }
)

export const tableCalculationEntriesSelector = createSelector(
  entriesSelector,
  tableCalculationSheetSelector,
  (entries, calculationSheets) => {
    const sheetIds = calculationSheets.flatMap(({ id, subSheets }) => [
      id,
      ...subSheets,
    ])
    const tableCalculationEntries = entries.filter(({ sheet_id }) =>
      sheetIds.includes(sheet_id)
    )
    return tableCalculationEntries
  }
)

export const summaryEntriesSelector = createSelector(
  entriesSelector,
  activeEntryIdSelector,
  (entries, guid) => {
    //todo: move this to utils
    const whiteListTypeForSummary = ['Sum', 'Number', 'Calculation']
    return entries.filter(
      ({ type, guid: entry_guid }) =>
        entry_guid != guid && whiteListTypeForSummary.includes(type)
    )
  }
)

export const tableCalculationScreenAndEntriesSelector = createSelector(
  sheetsSelector,
  simpleLoopCombinedSheetSelector,
  summaryEntriesSelector,
  (allSheets, simpleLoopSheets, eligibleEntries) => {
    return allSheets.reduce((screens, sheet) => {
      const { id, sheet_type_id } = sheet
      const sheetIds = [`${id}`]
      const canSkip = [LOOP_SHEET_TYPE_ID, LABELS_ABOVE_SHEET_TYPE_ID, LABELS_LEFT_SHEET_TYPE_ID].includes(sheet_type_id)

      const parentSheet = simpleLoopSheets.find(simpleLoop =>
        simpleLoop.subSheets.includes(id)
      )

      if (parentSheet) {
        sheetIds.push(`${parentSheet.id}`)
      }
      else if (canSkip) {
        // these sheets are either not eligible, as they are not in loops, or they are simple loops fields, so will be grouped with their children
        return screens
      }

      const entries = eligibleEntries.filter(({ sheet_id }) =>
        sheetIds.includes(`${sheet_id}`)
      )

      if (!entries.length) {
        return screens
      }

      screens.push({
        sheet: parentSheet || sheet,
        entries,
      })

      return screens
    }, [])
  }
)

export const tableSheetEntriesSelector = createSelector(
  entriesSelector,
  combinedSheetSelector,
  (entries, combinedSheets) => {
    // TODO: Should consider whether we need to remove advanced loop sheets from here and keep it to just table / simple loop sheets
    // There might be some breaking changes if we do this, so I am leaving it for now, but I think that table/simple loops and advanced
    // loops are two very different things with different processing required and should be treated as such
    const loopSheets = combinedSheets.filter(sheet => sheet?.subSheets?.length)

    return loopSheets.reduce((subSheetEntriesOnly, sheet) => {
      const { id, subSheets } = sheet
      subSheets.forEach(subSheetId => {
        subSheetEntriesOnly.push(
          ...entries.filter(
            entry => entry.sheet_id == subSheetId || entry.sheet_id == id
          )
        )
      })
      return subSheetEntriesOnly
    }, [])
  }
)

// selects keyfield + child sheet entries, given the outer sheet
export const activeTableSheetEntriesSelector = createSelector(
  entriesSelector,
  combinedSheetSelector,
  activeSheetIdSelector,
  (entries, combinedSheets, activeSheetId) => {
    const sheet = combinedSheets.find(({ id }) => id == activeSheetId)
    if (sheet) {
      const { subSheets = [] } = sheet

      const foundEntries = subSheets.map(sheet_id => {
        return entries.filter(({ sheet_id: id }) => id == sheet_id)
      })

      return foundEntries.length
        ? [
          ...foundEntries[0],
          ...entries.filter(({ sheet_id: id }) => id == activeSheetId),
        ]
        : [...entries.filter(({ sheet_id: id }) => id == activeSheetId)]
    } else {
      return []
    }
  }
)

// only eligible keyfield entries on the active child sheet
export const pickableKeyFieldEntries = createSelector(
  entriesSelector,
  activeSheetIdSelector,
  (entries, activeSheetId) =>
    entries.filter(({ sheet_id, type, style }) => {
      return sheet_id == activeSheetId && isEligibleKeyField({ type, style })
    })
)

// display field eligible entries
export const eligibleKeyFieldEntries = createSelector(
  activeTableSheetEntriesSelector,
  activeTableSheetEntries =>
    activeTableSheetEntries.reduce((eligibleEntries, entry) => {
      const { type, style, keyField } = entry
      if (keyField) {
        return [entry, ...eligibleEntries]
      } else if (isEligibleKeyField({ type, style })) {
        eligibleEntries.push(entry)
      }
      return eligibleEntries
    }, [])
)

// does not count advanced loops
export const activeFieldInTableSelector = createSelector(
  activeEntrySelector,
  tableSheetEntriesSelector,
  (activeEntry, tableEntries) => {
    if (!activeEntry) {
      return false
    }

    return tableEntries.filter(({ guid }) => guid == activeEntry.guid).length > 0
  }
)

// Finds all eligible condition or ref data screens for screens/fields in advanced loops
// Requires additional filtering depending on whether it's used for ref data, screen conditions, or field conditions
// NOTE: This selector should only be used when we know the active sheet/entry is part of an advanced loop - doesn't work for table screens or simple loops
export const advancedLoopEligibleScreensSelector = createSelector(
  activeAdvancedLoopSheetSelector,
  activeSheetSelector,
  sectionsSelector,
  standardSheetsSelector,
  simpleLoopCombinedSheetSelector,
  advancedLoopCombinedSheetSelector,
  (
    { subSheets, ...primarySheetAttributes },
    activeSheet,
    allSections,
    standardSheets,
    simpleLoopSheets,
    advancedLoopSheets
  ) => {
    const sectionMap = allSections.reduce((acc, section) => {
      return {
        ...acc,
        [section.id]: {
          depth: section.depth,
          position: section.position,
        },
      }
    }, {})

    const { depth: activeSheetDepth } = sectionMap[activeSheet.section_id]

    const allCombinedLoopSheets = [
      ...simpleLoopSheets,
      ...advancedLoopSheets,
    ].map(loopSheet => {
      const { subSheets } = loopSheet
      return {
        ...loopSheet,
        subSheets: subSheets.map(subSheet => {
          return typeof subSheet === 'object' ? subSheet.id : subSheet
        }),
      }
    })

    const simpleLoopChildSheetIds = simpleLoopSheets.flatMap(
      ({ subSheets }) => subSheets
    )

    const filteredSubSheets = subSheets.filter(subSheet => {
      const { depth: subSheetDepth } = sectionMap[subSheet.section_id]

      return (
        subSheetDepth <= activeSheetDepth &&
        (!simpleLoopChildSheetIds.includes(subSheet.id) ||
          subSheet.id == activeSheet.id)
      )
    })

    const filteredStandardSheets = standardSheets.filter(standardSheet => {
      // Remove any sub sheets to prevent duplicates
      if (subSheets.find(s => s.id == standardSheet.id)) return false

      const { depth: standardSheetDepth } = sectionMap[standardSheet.section_id]

      return (
        standardSheetDepth <= activeSheetDepth &&
        !simpleLoopChildSheetIds.includes(standardSheet.id)
      )
    })

    return [
      { ...primarySheetAttributes },
      ...filteredSubSheets,
      ...filteredStandardSheets,
    ].sort((a, b) => {
      // Use depth to determine if sheet is in a nested section; if so find and use top level section position for sort
      const { position: aSectionPosition, depth: aDepth } =
        sectionMap[a.section_id]
      let aPosition = aSectionPosition
      if (aDepth > 0) {
        const { section_id: aTopLevelSectionId } = allCombinedLoopSheets.find(
          cls => cls.subSheets.includes(a.id)
        )
        aPosition = sectionMap[aTopLevelSectionId].position
      }

        const { position: bSectionPosition, depth: bDepth } =
          sectionMap[b.section_id]
        let bPosition = bSectionPosition
        if (bDepth > 0) {
          const { section_id: bTopLevelSectionId } = allCombinedLoopSheets.find(
            cls => cls.subSheets.includes(b.id)
          )
          bPosition = sectionMap[bTopLevelSectionId].position
        }

        return aPosition - bPosition
    })
  }
)

/**
 *  Specifically for Calculation fields on a table.
 *  Pulls in entries from other normal screens and grabs that specific tables'
 *  elgible entries
 */
export const tableCalculationFieldsSelector = createSelector(
  standardPrimarySheetsSelector,
  subSheetsSelector,
  standardSheetEntriesSelector,
  activeEntrySelector,
  tableSheetEntriesSelector,
  primarySheetsSelector,
  (
    standardSheets,
    tableSheets,
    standardEntries,
    { guid: activeEntryGuid, sheet_id },
    tableEntries,
    primarySheets
  ) => {
    const entries = [...standardEntries, ...tableEntries]

    const sheets = [
      ...standardSheets,
      ...tableSheets.filter(sheet => sheet.id == sheet_id),
    ]

    const eligibleEntries = entries.filter(
      entry =>
        entry.guid != activeEntryGuid &&
        whiteListedTypesForCalculation.includes(entry.type)
    )

    return sheets.map(sheet => {
      const pSheet = primarySheets.find(
        pSheet => pSheet.multi_section === sheet.section_id
      )

      return {
        sheet: sheet,
        entries: eligibleEntries.filter(
          entry =>
            entry.sheet_id == sheet.id ||
            (pSheet && entry.sheet_id == pSheet.id)
        ),
        isSubSheet: tableSheets.find(tSheet => tSheet.id == sheet.id)
          ? true
          : false,
        primarySheet: pSheet,
      }
    })
  }
)

export const normalScreenRefDataSelector = createSelector(
  standardPrimarySheetsSelector,
  standardSheetEntriesSelector,
  activeEntrySelector,
  referenceDataEntries,
  (sheets, entries, { guid: activeEntryGuid } = {}, refEntries) => {
    const eligibleEntries = entries.filter(
      entry =>
        entry.guid != activeEntryGuid &&
        eligibleForReferenceData(entry) &&
        refEntries.find(r => r.guid == entry.guid)
    )

    return sheets.map(sheet => {
      return {
        sheet: sheet,
        entries: eligibleEntries.filter(entry => entry.sheet_id == sheet.id),
      }
    })
  }
)

export const tableScreenRefDataSelector = createSelector(
  standardPrimarySheetsSelector,
  subSheetsSelector,
  standardSheetEntriesSelector,
  activeEntrySelector,
  tableSheetEntriesSelector,
  primarySheetsSelector,
  referenceDataEntries,
  activeSheetIdSelector,
  (
    standardSheets,
    tableSheets,
    standardEntries,
    { guid: activeEntryGuid, sheet_id, keyField = false },
    tableEntries,
    primarySheets,
    refEntries,
    id
  ) => {
    const entries = [...standardEntries, ...tableEntries]

    const sheets = [
      ...standardSheets,
      ...(tableSheets.filter(sheet => sheet.id == sheet_id).length > 0
        ? tableSheets.filter(sheet => sheet.id == sheet_id)
        : primarySheets.filter(sheet => sheet.id == sheet_id)),
    ]

    const eligibleEntries = entries.filter(
      entry =>
        entry.guid != activeEntryGuid &&
        eligibleForReferenceData(entry) &&
        refEntries.find(r => r.guid == entry.guid)
    )

    return sheets.map(sheet => {
      const pSheet =
        primarySheets.find(
          pSheet => pSheet.multi_section === sheet.section_id
        ) ||
        tableSheets.find(
          t =>
            t.section_id == sheet.multi_section ||
            t.multi_section === sheet.section_id
        )

      return {
        sheet: sheet,
        entries: eligibleEntries.filter(
          entry =>
            entry.sheet_id == sheet.id ||
            (pSheet && entry.sheet_id == pSheet.id)
        ),
        isSubSheet: pSheet ? true : false,
        primarySheet: pSheet,
      }
    })
  }
)

export const advancedLoopScreenRefDataSelector = createSelector(
  entriesSelector,
  activeEntrySelector,
  advancedLoopEligibleScreensSelector,
  activeSheetParentHierarchySelector,
  referenceDataEntries,
  (
    allEntries,
    { guid: activeEntryGuid },
    refDataEligibleScreens,
    activeSheetParentIds,
    refEntries
  ) => {
    return refDataEligibleScreens.reduce((refDataScreensAndEntries, sheet) => {
      const eligibleRefDataEntries = allEntries.filter(
        entry =>
          entry.sheet_id == sheet.id &&
          entry.guid != activeEntryGuid &&
          eligibleForReferenceData(entry) &&
          refEntries.find(r => r.guid == entry.guid)
      )

      // Only show loop screens if they are in the parent hierarchy of the active sheet
      const isNonParentLoopScreen =
        isLoopScreen(sheet) && !activeSheetParentIds.includes(sheet.id)

      if (!isNonParentLoopScreen && eligibleRefDataEntries.length) {
        refDataScreensAndEntries.push({
          sheet: sheet,
          entries: eligibleRefDataEntries,
        })
      }
      return refDataScreensAndEntries
    }, [])
  }
)

export const calculationFieldsSelector = createSelector(
  standardPrimarySheetsSelector,
  standardSheetEntriesSelector,
  activeEntrySelector,
  (sheets, entries, { guid: activeEntryGuid }) => {
    const eligibleEntries = entries.filter(
      entry =>
        entry.guid != activeEntryGuid &&
        whiteListedTypesForCalculation.includes(entry.type)
    )

    return sheets.map(sheet => {
      return {
        sheet: sheet,
        entries: eligibleEntries.filter(entry => entry.sheet_id == sheet.id),
      }
    })
  }
)

// revised unified selector for eligible sheets, given an activeSheet
export const eligibleDependentSheetsSelector = createSelector(
  sheetsSelector,
  multiSectionIds,
  activeSheetIdSelector,
  (
    sheets,
    multiSectionIds,
    activeSheetId,
  ) => {
    let eligibleSheets = []
    const topLevelSheets = []
    const primaryLoopSheetsById = new Map()
    const subSheetsBySectionId = new Map()
    const parentSheetsBySectionId = new Map()
    let activeSheetIsSimpleTopLevel = false
    let activeSheet

    // build out lookups
    sheets.forEach(sheet => {
      const { id, multi_section, section_id } = sheet
      activeSheet = activeSheet || isTrueStringMatch(id, activeSheetId) && sheet

      if (!multiSectionIds.includes(section_id)) {
        // is a top-level sheet (no parent), normal or loop
        topLevelSheets.push(sheet)
        // is a normal screen (no multi_section & not a list)
        if (multi_section){
          parentSheetsBySectionId.set(multi_section, sheet)
        }
        else if (!multi_section && isStandardScreen(sheet)) {
          eligibleSheets.push(sheet)// top level normal screens are valid
          if (isTrueStringMatch(id, activeSheetId)) {
            activeSheetIsSimpleTopLevel = true
          }
        }
      }
      // top-level of loop or table
      else if (!multiSectionIds.includes(section_id)){
        primaryLoopSheetsById.set(id, sheet)
        parentSheetsBySectionId.set(multi_section, sheet)
      }
      else {
        if (!subSheetsBySectionId.has(section_id)) {
          subSheetsBySectionId.set(section_id, [])
        }
        subSheetsBySectionId.get(section_id).push(sheet)
        if (multi_section) {
          parentSheetsBySectionId.set(multi_section, sheet)
        }
      }
    })

    const { sheet_type_id, multi_section } = activeSheet || {}
    // (the one field on a list sheet can't be dependent or have anything depending on it). A list _screen_ could be conditional on an external field, so do not use this for screen conditions...
    const isListSheet = sheet_type_id === LOOP_SHEET_TYPE_ID && !multi_section
    // if the active sheet is a top-level normal sheet or a list sheet, eligibleSheets is correct already
    // if not, we need to start from the active sheet and work up and across
    if (!activeSheetIsSimpleTopLevel && !isListSheet) {
      let parentSheet = parentSheetsBySectionId.get(activeSheet.section_id)
      const activeSheetScreenHierarchy = []
      if (!parentSheet) {
        // activeSheet is a top-level loop/table sheet, so we just need its child sheets
        const subSheets = subSheetsBySectionId.get(multi_section) || []
        const validSubSheets = subSheets.filter(isStandardScreen)
        activeSheetScreenHierarchy.push(...validSubSheets)
      }
      else {
        // look up and across the multi_section
        while (parentSheet) {
          const subSheets = subSheetsBySectionId.get(parentSheet.multi_section) || []
          const validSubSheets = subSheets.filter(isStandardScreen)
          activeSheetScreenHierarchy.unshift(...validSubSheets)// add this sheet & and siblings
          const nextParentSheet = parentSheetsBySectionId.get(parentSheet.section_id)
          if (!nextParentSheet || !isTable(nextParentSheet)) {
            // if not a table and not the top sheet, add parent
            activeSheetScreenHierarchy.unshift(parentSheet)
          }
          parentSheet = nextParentSheet
        }
      }
      // active sheet and up hierarchy added first
      eligibleSheets.unshift(...activeSheetScreenHierarchy)
    }

    return eligibleSheets.map(sheet => {
      const { section_id } = sheet
      const parentSheet = parentSheetsBySectionId.get(section_id) || {}
      const isSimpleLoopSheet = Boolean(parentSheet && parentSheet.sheet_type_id === LOOP_SHEET_TYPE_ID)
      const isTableSubSheet = Boolean(parentSheet && isTable(parentSheet))
      const { description, placeholder } = isSimpleLoopSheet && isTableSubSheet ? parentSheet : sheet
      return {
        sheet,
        screenLabel: description || placeholder,
        parentSheet,
        isSubSheet: isSimpleLoopSheet || isTableSubSheet,
        isTableSubSheet,
      }
    })
  }
)

// selector for eligible sheets/entries, given an active entry
export const eligibleDependentEntriesSelector = createSelector(
  entriesSelector,
  activeEntryIdSelector,
  eligibleDependentSheetsSelector,
  (entries, activeEntryId, eligibleSheets) => {
    // build tableSheet lookup, exlcuding current active entry
    const tableSheets = new Map()
    eligibleSheets.forEach(screenConfig => {
      const { sheet, isTableSubSheet, parentSheet } = screenConfig
      // table sheet keyfields will group with fields under the table name
      if (isTableSubSheet) {
        tableSheets.set(parentSheet.id, sheet.id)
      }
    })

    // build entry lookup, exlcuding current active entry
    const entriesBySheetId = new Map()
    entries.forEach(entry => {
      const { guid, sheet_id } = entry
      if (guid !== activeEntryId) {
        const whichSheetId = tableSheets.get(sheet_id) ? tableSheets.get(sheet_id) : sheet_id
        if (!entriesBySheetId.has(whichSheetId)) {
          entriesBySheetId.set(whichSheetId, [])
        }
        entriesBySheetId.get(whichSheetId).push(entry)
      }
    })

    return eligibleSheets.reduce((acc, screenConfig) => {
      const { sheet } = screenConfig
      const entries = entriesBySheetId.get(sheet.id) || []
      if (!entries.length) {
        return acc
      }
      acc.push({
        ...screenConfig,
        entries: entries.sort(orderByPosition),
      })
      return acc
    }, [])
  }
)

// given any sheetId in the CURRENT hierarchy, calculates:
// reachableBy: all sheets w/ fields that can reach/depend on a field on sheetId (across & downwards)
// canReach: all sheets w/ fields that a field on sheetId could depend upon/reach (across & upwards)
export const canReachSheetSelector = createSelector(
  sheetsSelector,
  standardPrimarySheetsSelector,
  (sheets, standardPrimarySheets) => {
    // Extract the IDs of top-level normal sheets
    const standardPrimarySheetIds = new Set(standardPrimarySheets.map(sheet => sheet.id))

    return (sheetId) => {
      // top level normal sheets are always reachable, so if the sheet is a
      // top level sheet, include it and all top level sheets by default
      const wasMovedToAPrimarySheet = standardPrimarySheetIds.has(sheetId)
      const reachableByDefault = wasMovedToAPrimarySheet
        ? sheets.map(({ id }) => id)// new sheet is normal top-level, so ALL sheets can reach it
        : [sheetId]// sheet starts out only being able to reach itself
      const canReachDefault = wasMovedToAPrimarySheet
        ? standardPrimarySheetIds// new sheet is normal top-level, so it can only reach any normal top level normal sheet
        : [...standardPrimarySheetIds, sheetId]// by default, sheet only reach itself and all top level normal sheets

      const reachableBy = new Set(reachableByDefault)
      const canReach = new Set(canReachDefault)
      const targetSheet = sheets.find(({ id }) => isTrueStringMatch(id, sheetId))

      // A keyfield sheet can reach any direct child sheets & vice-versa
      sheets.forEach(sheet => {
        if (targetSheet.multi_section === sheet.section_id) {
          canReach.add(sheet.id)
          reachableBy.add(sheet.id)
        }
      })

      let isFirstLoop = true
      let multiSectionsBelow = []
      // find parent and parallel sheets, if necessary
      if (!wasMovedToAPrimarySheet) {
        let parentSheet = sheets.find(
          sheet => targetSheet.section_id === sheet.multi_section
        )
        while (parentSheet) {
          // Add the parent sheet to reachableBy and canReach
          reachableBy.add(parentSheet.id)
          canReach.add(parentSheet.id)

          // add siblings at this level
          // e.g. those that have the same section as the multi_section
          sheets.forEach(sheet => {
            if (parentSheet.multi_section === sheet.section_id) {
              // All parallel/sibling sheets that can be reached
              if (!sheet.multi_section) {
                canReach.add(sheet.id)
              }
              if (isFirstLoop) {
                // reachableBy logic going up only applies to immediate siblings
                reachableBy.add(sheet.id)
                // now add all sheets below to reachableBy
                if (sheet.multi_section) {
                  multiSectionsBelow.push(sheet.multi_section)
                }
              }
            }
          })

          // Look for any parent sheets of parentSheet
          parentSheet = sheets.find(
            sheet => parentSheet.section_id == sheet.multi_section
          )
        }
        // get sheets lower in the direct hierarchy that can reach the target sheet
        if (multiSectionsBelow.length) {
          getAllDescendants(multiSectionsBelow, sheets, reachableBy)
        }
      }

      return {
        reachableBy: Array.from(reachableBy),
        canReach: Array.from(canReach),
      }
    }
  }
)

export const mirrorFieldsAndScreenTableSelector = createSelector(
  standardPrimarySheetsSelector,
  subSheetsSelector,
  standardSheetEntriesSelector,
  activeEntrySelector,
  tableSheetEntriesSelector,
  primarySheetsSelector,
  (
    standardSheets,
    tableSheets,
    standardEntries,
    { guid: activeEntryGuid, sheet_id },
    tableEntries,
    primarySheets
  ) => {
    const entries = [...standardEntries, ...tableEntries]

    const sheets = [
      ...standardSheets,
      ...tableSheets.filter(sheet => sheet.id == sheet_id),
    ]

    const eligibleEntries = entries.filter(
      entry =>
        entry.guid != activeEntryGuid &&
      !blackListedTypesForMirror.includes(entry.type)
    )

    return sheets.map(sheet => {
      const pSheet = primarySheets.find(
        pSheet => pSheet.multi_section === sheet.section_id
      )

      return {
        sheet: sheet,
        entries: eligibleEntries.filter(
          entry =>
            entry.sheet_id == sheet.id ||
            (pSheet && entry.sheet_id == pSheet.id)
        ),
        isSubSheet: tableSheets.find(tSheet => tSheet.id == sheet.id)
          ? true
          : false,
        primarySheet: pSheet,
      }
    })
  })

export const mirrorFieldsAndScreenSelector = createSelector(
  standardPrimarySheetsSelector,
  standardSheetEntriesSelector,
  activeEntrySelector,
  (sheets, entries, { guid: activeEntryGuid }) => {
    const eligibleEntries = entries.filter(
      entry =>
        entry.guid != activeEntryGuid &&
        !blackListedTypesForMirror.includes(entry.type)
    )

    return sheets.map(sheet => {
      return {
        sheet: sheet,
        entries: eligibleEntries.filter(entry => entry.sheet_id == sheet.id),
      }
    })
  }
)

export const keyFieldTypesSelector = createSelector(entryTypesSelector, types =>
  types.filter(({ type }) => keyFieldEligibleTypes.includes(type))
)

export const alphaKeyFieldTypesSelector = createSelector(
  keyFieldTypesSelector,
  types => types.sort((a, b) => a.description.localeCompare(b.description))
)

export const paymentFieldScreenSelector = createSelector(
  combinedSheetSelector,
  standardSheetEntriesSelector,
  activeEntrySelector,
  (sheets, entries, { guid: activeEntryGuid }) => {
    const eligibleEntries = entries.filter(
      entry =>
        entry.guid != activeEntryGuid &&
        whiteListedTypesForPaymentField.includes(entry.type) &&
        checkNumberStyleForPaymentTotalField(entry)
    )

    const filteredSheets = sheets
      .map(sheet => {
        if (
          eligibleEntries.filter(entry => entry.sheet_id == sheet.id).length > 0
        )
          return sheet
      })
      .filter(a => a)
    return filteredSheets
  }
)

export const paymentFieldScreenAndEntriesSelector = createSelector(
  combinedSheetSelector,
  standardSheetEntriesSelector,
  activeEntrySelector,
  (sheets, entries, { guid: activeEntryGuid }) => {
    const eligibleEntries = entries.filter(
      entry =>
        entry.guid != activeEntryGuid &&
        whiteListedTypesForPaymentField.includes(entry.type) &&
        checkNumberStyleForPaymentTotalField(entry)
    )

    const eligibleSheetIds = eligibleEntries.reduce((acc, { sheet_id }) => {
      if (!acc.includes(sheet_id)) {
        return [...acc, `${sheet_id}`]
      }
      return acc
    }, [])

    return sheets.reduce((acc, sheet) => {
      if (eligibleSheetIds.includes(`${sheet.id}`)) {
        acc.push({
          sheet,
          entries: eligibleEntries.filter(
            ({ sheet_id }) => `${sheet_id}` === `${sheet.id}`
          ),
        })
      }
      return acc
    }, [])
  }
)

export const paymentFieldDataToIncludeTableSelector = createSelector(
  entriesSelector,
  activeEntrySelector,
  activeSheetSelector,
  sectionPreviewSelector,
  subSheetsSelector,
  (entries, { guid, position }, currentSheet, sheets, subSheets) => {
    const currentSheetIndex = sheets.findIndex(
      sheet => sheet.id == currentSheet.id
    )

    const childSheet =
      subSheets.find(s => s.section_id == currentSheet.multi_section) || {}

    const sheetsToSearch = sheets
      .map(({ id }, index) => {
        if (index <= currentSheetIndex) {
          return id
        }
      })
      .filter(a => a)

    const combinedSheetIds = [...sheetsToSearch, childSheet.id]

    return entries.filter(entry => {
      if (
        entry.sheet_id != currentSheet.id &&
        combinedSheetIds.includes(entry.sheet_id) &&
        guid !== entry.guid &&
        !paymentDataToIncludeBlacklist.includes(entry.type) &&
        !entry.keyField
      )
        return entry
      else if (
        entry.sheet_id == currentSheet.id &&
        sheetsToSearch.includes(entry.sheet_id) &&
        guid !== entry.guid &&
        !paymentDataToIncludeBlacklist.includes(entry.type) &&
        position > entry.position &&
        !entry.keyField
      ) {
        return entry
      }
    })
  }
)

export const paymentFieldDataToIncludeSelector = createSelector(
  entriesSelector,
  activeEntrySelector,
  activeSheetSelector,
  sectionPreviewSelector,
  (entries, { guid, position }, currentSheet, sheets) => {
    const currentSheetIndex = sheets.findIndex(
      sheet => sheet.id == currentSheet.id
    )

    const sheetsToSearch = sheets
      .map(({ id, subSheets }, index) => {
        if (index <= currentSheetIndex) {
          return subSheets.length ? subSheets[0] : id
        }
      })
      .filter(a => a)

    return entries.filter(entry => {
      if (
        entry.sheet_id != currentSheet.id &&
        sheetsToSearch.includes(entry.sheet_id) &&
        guid !== entry.guid &&
        !paymentDataToIncludeBlacklist.includes(entry.type) &&
        !entry.keyField
      )
        return entry
      else if (
        entry.sheet_id == currentSheet.id &&
        sheetsToSearch.includes(entry.sheet_id) &&
        guid !== entry.guid &&
        !paymentDataToIncludeBlacklist.includes(entry.type) &&
        position > entry.position &&
        !entry.keyField
      ) {
        return entry
      }
    })
  }
)

// This is semi-gross, but works.
const getNextPosition = (sheet = {}, entries = []) => {
  const { subSheets = [] } = sheet
  const id = subSheets.length ? subSheets[subSheets.length - 1] : sheet.id
  const sheetEntries = entries
    .filter(entry => entry.sheet_id === id)
    .sort(orderByPosition)
  const lastEntry = sheetEntries[sheetEntries.length - 1]

  return {
    sheet_id: id,
    position: lastEntry ? lastEntry.position + 1 : 0,
  }
}

export const nextFieldLocation = createSelector(
  activeSheetIdSelector,
  sectionPreviewSelector,
  entriesSelector,
  sheetsSelector,
  (activeSheetId, previewSheets, allEntries, allSheets) => {
    const activeSheet = previewSheets.find(({ id }) => id === activeSheetId)
    let sheet = activeSheet || previewSheets[previewSheets.length - 1]

    if (sheet && sheet.sheet_type_id === ADVANCED_LOOP_SHEET_TYPE_ID) {
      sheet = {
        ...sheet,
        subSheets: findChildSheets(allSheets, sheet.multi_section).map(
          ({ id }) => id
        ),
      }
    }

    return getNextPosition(sheet, allEntries)
  }
)

export const entryConditions = createSelector(
  activeEntryIdSelector,
  conditionsSelector,
  (id, conditions = []) =>
    conditions.filter(({ parentEntryGuid }) =>
      isTrueStringMatch(parentEntryGuid, id)
    )
)

export const sheetConditions = createSelector(
  activeSheetIdSelector,
  conditionsSelector,
  (id, conditions = []) =>
    conditions.filter(
      ({ parentEntryGuid, sheet_id }) =>
        !parentEntryGuid && isTrueStringMatch(sheet_id, id)
    )
)

export const saveAppSelector = createSelector(entitiesSelector, entities => ({
  form: assembleForm(entities),
}))

export const pastEntitiesSelector = createSelector(entities, pastSelector)

export const futureEntitiesSelector = createSelector(entities, futureSelector)

export const publishUsersSelector = createSelector(settings, ({ users }) => {
  return users
})

export const publishGroupsSelector = createSelector(settings, ({ groups }) => {
  return groups
})

export const saveAndPublishLoadedSelector = createSelector(
  settings,
  ({ loaded }) => {
    return loaded
  }
)

export const isSaveAndPublishLoadingSelector = createSelector(
  settings,
  ({ isLoading }) => {
    return isLoading
  }
)

export const saveAndPublishSendNotificationSelector = createSelector(
  settings,
  ({ send_notification }) => {
    return send_notification
  }
)

export const isPublishActionPendingSelector = createSelector(
  settings,
  ({ isPublishPending }) => {
    return isPublishPending
  }
)

export const saveAndPublishAssignedUsersSelector = createSelector(
  settings,
  ({ assignedUsers }) => assignedUsers
)

export const saveAndPublishAssignedGroupsSelector = createSelector(
  settings,
  ({ assignedGroups }) => assignedGroups
)

export const ftuxVideoIDSelector = createSelector(
  settings,
  ({ ftuxVideoID }) => ftuxVideoID
)

export const onboardUserSelector = createSelector(
  settings,
  ({ onboardingUser }) => onboardingUser
)

export const folderIdSelector = createSelector(
  settings,
  ({ folder_id }) => folder_id
)

export const nonTableEntriesSelector = createSelector(
  entriesSelector,
  tableSheetEntriesSelector,
  (entries, tableSheetEntries) => {
    const sheetEntryGuids = new Set(tableSheetEntries.map(({ guid }) => guid))
    return entries.filter(({ guid }) => !sheetEntryGuids.has(guid))
  }
)

export const whiteLabelSelector = createSelector(
  settings,
  ({ whiteLabeled }) => whiteLabeled
)

export const whiteLabelSettingsSelector = ({ whiteLabelSettings }) =>
  whiteLabelSettings

export const whiteLabelPrimaryLogoSelector = createSelector(
  whiteLabelSettingsSelector,
  ({ primaryLogo }) => primaryLogo
)

export const whiteLabelSecondaryLogoSelector = createSelector(
  whiteLabelSettingsSelector,
  ({ secondaryLogo }) => secondaryLogo
)

export const prefilledEntriesSelector = createSelector(
  clientEntriesSelector,
  clientEntries => clientEntries.filter(({ preFillEntry }) => preFillEntry)
)

export const topLevelSheetEntriesSelector = createSelector(
  entriesSelector,
  primarySheetsSelector,
  (entries, sheets) => {
    const sheetIds = new Set(sheets.map(({ id }) => id))
    return entries.filter(({ sheet_id }) => sheetIds.has(sheet_id))
  }
)

export const hipaaCompliantSelector = createSelector(
  settings,
  ({ hipaaCompliant }) => hipaaCompliant
)
